import clsx from 'clsx'
import { useState } from 'react'
import useBreakpoint from 'src/common/hooks/ui/useBreakpoint'
import { useChainFeatures } from 'src/common/hooks/web3/useChainFeatures'
import { useAtomValue } from 'jotai'
import { Outlet, useNavigate } from 'react-router-dom'
import Fade from 'src/common/ui-kit/Animations/Fade'
import Drawer from 'src/common/ui-kit/Drawer'
import Tabs, { Tab } from 'src/common/ui-kit/Tabs/Tabs'
import OffersToTake from './components/OffersToTake/OffersToTake'
import TradeOption from './components/TradeOption'
import useTradePageState from './hooks/useTradePageState'
import {
  bestOffersAtom,
  buyTokenAmountAtom,
  buyTokenAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
  showOffersToTakeAtom,
} from './stores/trade-store'
import { OfferWithTake } from 'src/common/utils/offers/findBestOffers'
import { AnyToken } from 'src/common/types/models/Tokens'

const tabs: Tab[] = [
  { name: 'Swap', path: '/trade/swap' },
  { name: 'Limit / OTC', path: '/trade/limit' },
]

/** Renders the "View Offers" button and drawer for mobile */
interface MobileOffersButtonProps {
  onOpen(): void
  onClose(): void
  isOpen: boolean
}

const MobileOffersButton: React.FC<MobileOffersButtonProps> = ({
  onOpen,
  isOpen,
  onClose,
}) => {
  return (
    <Fade className='flex justify-center items-center mt-2'>
      <div
        onClick={onOpen}
        className='text-button text-primary hover:text-primary-accent cursor-pointer'
      >
        View Offers
      </div>
      <Drawer open={isOpen} onClose={onClose}>
        <OffersToTake className='mt-4' />
      </Drawer>
    </Fade>
  )
}

/** Renders the Options block on desktop */
interface DesktopOptionsProps {
  className?: string
  isSwapMode: boolean
  bestOffers: OfferWithTake[]
  isLimitMode: boolean
  buyToken: AnyToken | undefined
  buyTokenAmount: Big
  sellToken: AnyToken | undefined
  sellTokenAmount: Big
  shouldShowOffersToTake: boolean
}

const DesktopOptions: React.FC<DesktopOptionsProps> = ({
  className,
  shouldShowOffersToTake,
  isSwapMode,
  bestOffers,
  isLimitMode,
  buyToken,
  buyTokenAmount,
  sellToken,
  sellTokenAmount,
}) => {
  const navigate = useNavigate()

  return (
    <div className={className}>
      <Fade className='paper p-4 col-span-1 flex flex-col gap-4 self-start'>
        <span className='typo-title2 text-main-accent'>Options:</span>
        <div className='flex flex-col gap-3'>
          <TradeOption
            mode='swap'
            active={isSwapMode && bestOffers.length > 0}
            disabled={bestOffers.length === 0}
            title='Swap instantly'
            badgeIcon=''
            badgeText='Executes Instantly'
            buyToken={buyToken}
            buyTokenAmount={buyTokenAmount}
            sellToken={sellToken}
            sellTokenAmount={sellTokenAmount}
            offers={bestOffers}
            onClick={() => navigate('swap')}
          />
          <TradeOption
            mode='limit'
            active={isLimitMode}
            title='Limit offer'
            description='Get a better price by placing a Limit. Limits are added to the orderbook and can be cancelled at any time.'
            badgeIcon=''
            badgeText='Executes when matched'
            buyToken={buyToken}
            buyTokenAmount={buyTokenAmount}
            sellToken={sellToken}
            sellTokenAmount={sellTokenAmount}
            onClick={() => navigate('limit')}
          />
        </div>
      </Fade>
      {shouldShowOffersToTake && (
        <Fade className='paper p-4 col-span-1 self-start mt-2'>
          <OffersToTake />
        </Fade>
      )}
    </div>
  )
}

const TradePage = () => {
  const { isLimitMode, isSwapMode, showTabs } = useTradePageState()
  const { features } = useChainFeatures()

  const sellToken = useAtomValue(sellTokenAtom)
  const sellTokenAmount = useAtomValue(sellTokenAmountAtom)
  const buyToken = useAtomValue(buyTokenAtom)
  const buyTokenAmount = useAtomValue(buyTokenAmountAtom)
  const bestOffers = useAtomValue(bestOffersAtom)
  const showOffersToTakeState = useAtomValue(showOffersToTakeAtom)

  const [showOffersModal, setShowOffersModal] = useState(false)

  const upToLg = useBreakpoint('lg')
  const isDesktop = !upToLg

  // Determine if offer exists
  const offerFilled = sellToken && buyToken && sellTokenAmount.gt(0)
  const shouldShowOffersToTake =
    showOffersToTakeState &&
    showTabs &&
    bestOffers.length > 0 &&
    features.tradeOptions &&
    isSwapMode

  const showViewOffersButton =
    showTabs &&
    bestOffers.length > 0 &&
    features.tradeOptions &&
    isSwapMode &&
    upToLg

  // Grid container style for the desktop layout
  const desktopGridStyle = {
    maxWidth: '1240px',
    gridTemplateColumns: '1fr minmax(33%, 420px) 1fr',
    padding: '0 16px',
    ...(!offerFilled && { gridAutoColumns: '50%' }),
  }

  return (
    <Fade
      transition={{ delay: 0.2 }}
      className={clsx('container mx-auto grid gap-3 justify-center', {
        'grid-cols-3': offerFilled,
      })}
      style={desktopGridStyle}
    >
      <div></div>
      {showTabs && <Tabs className='col-span-full col-start-2' tabs={tabs} />}
      <div className='col-start-2'>
        <Outlet />
        {showViewOffersButton && (
          <MobileOffersButton
            onOpen={() => setShowOffersModal(true)}
            isOpen={showOffersModal}
            onClose={() => setShowOffersModal(false)}
          />
        )}
      </div>
      {isDesktop && showTabs && offerFilled && features.tradeOptions && (
        <DesktopOptions
          isSwapMode={isSwapMode}
          bestOffers={bestOffers}
          isLimitMode={isLimitMode}
          buyToken={buyToken}
          buyTokenAmount={buyTokenAmount}
          sellToken={sellToken}
          sellTokenAmount={sellTokenAmount}
          shouldShowOffersToTake={shouldShowOffersToTake}
        />
      )}
    </Fade>
  )
}

export default TradePage
